import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
interface GoNoGoAddCriterionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: {
    category: string
    title: string
    description: string
    weight: number
    isMandatory: boolean
  }) => void
  isLoading?: boolean
  categories: { value: string; label: string }[]
}

export function GoNoGoAddCriterionDialog({ open, onOpenChange, onConfirm, isLoading, categories }: GoNoGoAddCriterionDialogProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState(categories[0]?.value ?? 'capability')
  const [weight, setWeight] = useState(5)
  const [isMandatory, setIsMandatory] = useState(false)

  const handleConfirm = () => {
    if (!title.trim()) return
    onConfirm({ category, title: title.trim(), description: description.trim(), weight, isMandatory })
    // Reset form
    setTitle('')
    setDescription('')
    setCategory(categories[0]?.value ?? 'capability')
    setWeight(5)
    setIsMandatory(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Criterion</DialogTitle>
          <DialogDescription>Add a custom criterion to this assessment.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1.5">Criterion name</label>
            <Input
              placeholder="e.g., Subcontractor availability"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium block mb-1.5">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1.5">Weight (1-10)</label>
            <div className="flex items-center gap-1">
              {Array.from({ length: 10 }, (_, i) => i + 1).map(w => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWeight(w)}
                  className={`h-8 w-8 rounded-lg text-xs border transition-colors ${
                    w === weight
                      ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))] font-semibold border-transparent'
                      : 'text-muted-foreground hover:bg-muted border-border'
                  }`}
                >
                  {w}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Higher weight = more influence on the overall score.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium block mb-1.5">
              Guidance <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-transparent resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              rows={2}
              placeholder="Scoring guidance — what does a 5 look like vs. a 1?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox
              checked={isMandatory}
              onCheckedChange={(checked) => setIsMandatory(checked === true)}
            />
            <div>
              <span className="text-sm font-medium">Mandatory criterion</span>
              <p className="text-[10px] text-muted-foreground">Score of 1 will flag an automatic No go warning.</p>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!title.trim() || isLoading}>
            {isLoading ? 'Adding...' : 'Add Criterion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
